import React, { FunctionComponent } from "react";
import { Grid, Link } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";

import { sec2hms } from "/src/utils/time-utils";
import ViewTrackButton from "./ViewTrackButton";
import {
  SingleRaceRecord,
  DsqRecord,
  instanceOfDsqRecord,
  ChallengeFormula,
  MaxDistanceRaceData,
  instanceOfSingleRaceRecord,
} from "/src/domain/challenges/ChallengeProtocolData";
import { trimName } from "/src/domain/shared/trimName";
import { links } from "/src/domain/shared/links";

export type TrackViewProps = {
  record: SingleRaceRecord | DsqRecord;
};

export const TrackView: FunctionComponent<TrackViewProps> = ({ record }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const startParametersMap = record.time ? 1 : 2;

  return (
    <div className={classes.parametersBlock}>
      <div className={classes.parametersMore}>
        <>
          {Boolean(record.comment) && (
            <Grid container>
              <Grid item xs={5}>
                {t("comment")}
              </Grid>
              <Grid item xs={7} className={classes.parameterValue}>
                {record.comment}
              </Grid>
            </Grid>
          )}
        </>
        <>
          {Boolean(record.trackId) && (
            <Grid container>
              <Grid item xs={5}>
                TrackID
              </Grid>
              <Grid item xs={7} className={classes.parameterValue}>
                {record.trackId}
              </Grid>
            </Grid>
          )}
        </>
        <>
          {Boolean(record.time) && (
            <Grid container>
              <Grid item xs={5}>
                {t("time")}
              </Grid>
              <Grid item xs={7} className={classes.parameterValue}>
                {sec2hms(record.time!!)}
              </Grid>
            </Grid>
          )}
        </>
        <>
          {Boolean(record.absolute) && (
            <Grid container>
              <Grid item xs={5}>
                {t("absolute")}
              </Grid>
              <Grid item xs={7} className={classes.parameterValue}>
                {sec2hms(record.absolute!!)}
              </Grid>
            </Grid>
          )}
        </>
        {instanceOfDsqRecord(record) && (
          <>
            {Boolean(record.moving_time) && (
              <Grid container>
                <Grid item xs={5}>
                  {t("movingTime")}
                </Grid>
                <Grid item xs={7} className={classes.parameterValue}>
                  {sec2hms(record.moving_time!!)}
                </Grid>
              </Grid>
            )}
            {Boolean(record.elapsed_time) && (
              <Grid container>
                <Grid item xs={5}>
                  {t("elapsedTime")}
                </Grid>
                <Grid item xs={7} className={classes.parameterValue}>
                  {sec2hms(record.elapsed_time!!)}
                </Grid>
              </Grid>
            )}
          </>
        )}
        {record.parameters.map((parameter) => {
          if (startParametersMap > parameter.index || !parameter.value)
            return null;
          return (
            <Grid key={parameter.index} container>
              <Grid item xs={5}>
                {t(parameter.label)}
              </Grid>
              <Grid item xs={7} className={classes.parameterValue}>
                {parameter.label == "time" ||
                parameter.label == "absolute" ||
                parameter.label == "pace"
                  ? sec2hms(parameter.value as number)
                  : parameter.value}
              </Grid>
            </Grid>
          );
        })}
      </div>
      <ViewTrackButton trackId={record.trackId} />
    </div>
  );
};

export type MultipleTrackViewProps = {
  className?: string;
  record: SingleRaceRecord;
  challengeType: ChallengeFormula;
  isExpanded: boolean;
};

export const MultipleTrackView: FunctionComponent<MultipleTrackViewProps> = ({
  challengeType,
  record,
  isExpanded,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const trackData = record.data as MaxDistanceRaceData;

  if (!isExpanded) {
    return <></>;
  }

  return (
    <div className={classes.parametersBlock}>
      {trackData.tracks.map((track) => (
        <div className={classes.multipleTrackViewContainer} key={track.id}>
          <div className={classes.multipleTrackViewWrapper}>
            {challengeType === ChallengeFormula.TEAM_MAX_DISTANCE 
              && !!record.participant 
              && <Grid container>
                  <Grid item xs={5}>
                    {t("name")}
                  </Grid>
                  <Grid 
                    item 
                    xs={7} 
                    component={Link} 
                    href={`${links.origin}/profile?${record.participant.id}`}
                    className={classes.parameterValue}
                  >
                    {trimName(record.participant.firstName, record.participant.lastName)}
                  </Grid>
                </Grid>}
            <Grid container>
              <Grid item xs={5}>
                {t("activityStart")}
              </Grid>
              <Grid item xs={7} className={classes.parameterValue}>
                {track.start_date_abs}
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={5}>
                {t("distance")}
              </Grid>
              <Grid item xs={7} className={classes.parameterValue}>
                {track.distance}
              </Grid>
            </Grid>
          </div>
          <ViewTrackButton trackId={track.id} />
        </div>
      ))}
    </div>
  );
};

const useStyles = makeStyles({
  parameters: {
    marginTop: "0.2rem",
    fontSize: "0.75rem",
    "&>div": {
      padding: "0.2rem 0",
    },
  },
  parametersBlock: {
    marginTop: "0.5rem",
    fontWeight: 500,
    fontSize: "0.9rem",
  },
  parametersMore: {
    marginBottom: "1rem",
  },
  parameterValue: {
    fontSize: "0.8rem",
  },
  comment: {
    paddingBottom: "1rem",
  },
  commentHeader: {
    marginBottom: "0.5rem",
  },
  commentValue: {
    fontSize: "0.8rem",
    color: "rgba(83, 83, 83, 1)",
  },
  bolderText: {
    fontWeight: 600,
  },
  multipleTrackViewContainer: {
    marginBottom: "1.5rem",
  },
  multipleTrackViewWrapper: {
    marginBottom: "1rem",
  },
});
